import {autoserializeAs, autoserializeAsJson, autoserializeUsing, inheritSerialization} from "dcerialize";
import type {DateTime} from "luxon";
import type {Constructor} from "type-fest";
import {DateTimeSerializer} from "../../helpers/DateSerializers";
import {nonEmpty} from "../../helpers/OptionalStringDecorators";
import {IdOnlyBactoforceLocation, SimpleBactoforceLocation} from "../bactoforce/BactoforceLocation";
import {
  asNew,
  createExisting,
  createNew,
  type Existing,
  type IdentifiableModel,
  type IdOf,
  type New,
} from "../base/IdentifiableModel";
import type {ModificationTrackedModel} from "../base/ModificationTrackedModel";
import type {SoftDeletableModel} from "../base/SoftDeletableModel";
import {CodeOnlyLanguage, SimpleLanguage} from "../i18n/Language";
import {ModificationDetails} from "../ModificationDetails";
import {IdOnlyThirdPartyCompany, SimpleThirdPartyCompany} from "../third-party-companies/ThirdPartyCompany";
import {WritableCustomerUserLocationAssignment} from "./UserAssignment";

export enum UserAccountType {
  Admin = "admin",
  Bactoforce = "bactoforce",
  Customer = "customer",
  ThirdParty = "third_party",
}

export type UserAccountTypeRouteComponent = "admins" | "bactoforce" | "customers" | "third-party";

export function routeComponentFromUserType(userType: UserAccountType): UserAccountTypeRouteComponent {
  switch (userType) {
    case UserAccountType.Admin:
      return "admins";
    case UserAccountType.Bactoforce:
      return "bactoforce";
    case UserAccountType.Customer:
      return "customers";
    case UserAccountType.ThirdParty:
      return "third-party";
  }
}

export class IdOnlyUser implements IdentifiableModel {
  @autoserializeAs(() => Number)
  public id?: number;

  public static existing(id: IdOf<IdOnlyUser>): Existing<IdOnlyUser> {
    return createExisting(IdOnlyUser, id);
  }
}

export interface TypedUser<TUserType extends UserAccountType = UserAccountType> {
  readonly type: TUserType;
}

export type TypedIdOnlyUser<TUserType extends UserAccountType = UserAccountType> = IdOnlyUser & TypedUser<TUserType>;

@inheritSerialization(() => IdOnlyUser)
export class SimpleUser extends IdOnlyUser {
  @autoserializeAs(() => String)
  @nonEmpty
  public title?: string;
  @autoserializeAs(() => String)
  public firstName: string;
  @autoserializeAs(() => String)
  public lastName: string;

  // This must not be renamed - it matches the model field in the backend!
  public get fullName(): string {
    return `${this.lastName}, ${this.firstName}`;
  }

  public get displayName(): string {
    const name = `${this.firstName} ${this.lastName}`;
    return this.title !== undefined ? `${this.title} ${name}` : name;
  }
}

@inheritSerialization(() => SimpleUser)
export class ContactableUser extends SimpleUser {
  @autoserializeAs(() => String)
  public email: string;
  @autoserializeAs(() => String)
  @nonEmpty
  public phone1?: string;
  @autoserializeAs(() => String)
  @nonEmpty
  public phone2?: string;
  @autoserializeAs(() => String)
  @nonEmpty
  public mobilePhone?: string;
}

@inheritSerialization(() => ContactableUser)
export abstract class BaseCommonUser extends ContactableUser implements TypedUser, SoftDeletableModel {
  @autoserializeAsJson()
  public readonly type: UserAccountType;

  @autoserializeAs(() => CodeOnlyLanguage)
  public language: Existing<CodeOnlyLanguage>;
  @autoserializeAs(() => String)
  @nonEmpty
  public position?: string;
  @autoserializeAs(() => String)
  @nonEmpty
  public fax?: string;

  @autoserializeAs(() => String)
  @nonEmpty
  public remarks?: string;

  @autoserializeUsing(DateTimeSerializer)
  public readonly lastLoggedInAt?: DateTime<true>;
  @autoserializeAs(() => Boolean)
  public readonly isDeleted: boolean;

  // public for serialization but should only be used by subclasses!
  public constructor() {
    super();
    this.isDeleted = false;
  }
}

/**
 * @sealed
 */
@inheritSerialization(() => BaseCommonUser)
export class CommonUser extends BaseCommonUser {
  @autoserializeAs(() => SimpleLanguage)
  public override language: Readonly<Existing<SimpleLanguage>>;
}

@inheritSerialization(() => BaseCommonUser)
export abstract class BaseUser<TUserType extends UserAccountType>
  extends BaseCommonUser
  implements TypedUser<TUserType>, ModificationTrackedModel {
  @autoserializeAsJson()
  public abstract override readonly type: TUserType;

  @autoserializeAs(() => ModificationDetails)
  public readonly modificationDetails?: ModificationDetails;

  protected static _createNew<TUser extends BaseUser<TUserType>, TUserType extends UserAccountType>(
    this: Constructor<TUser>,
    firstName: string,
    lastName: string,
    email: string,
    language: Readonly<Existing<CodeOnlyLanguage>>,
  ): New<TUser> {
    const user = createNew(this);
    user.firstName = firstName;
    user.lastName = lastName;
    user.email = email;
    user.language = language;
    return user;
  }
}

export type IdOnlyBactoforceUser = IdOnlyUser;

// Does not exist in the backend
@inheritSerialization(() => BaseUser)
export abstract class BaseWritableBactoforceUser<TBfUserType extends UserAccountType.Bactoforce | UserAccountType.Admin>
  extends BaseUser<TBfUserType> {
  @autoserializeAs(() => Boolean)
  public isInspector: boolean;

  @autoserializeAs(() => IdOnlyBactoforceLocation)
  public primaryBactoforceLocation: Existing<IdOnlyBactoforceLocation>;
}

@inheritSerialization(() => BaseWritableBactoforceUser)
export class WritableBactoforceUser extends BaseWritableBactoforceUser<UserAccountType.Bactoforce> {
  @autoserializeAsJson()
  public override readonly type = UserAccountType.Bactoforce;

  public static createNew(firstName: string,
                          lastName: string,
                          email: string,
                          language: Readonly<Existing<CodeOnlyLanguage>>,
                          isInspector: boolean,
                          primaryBactoforceLocation: Readonly<Existing<IdOnlyBactoforceLocation>>): New<WritableBactoforceUser> {
    const user = this._createNew(firstName, lastName, email, language);
    user.isInspector = isInspector;
    user.primaryBactoforceLocation = primaryBactoforceLocation;
    return user;
  }
}

/**
 * @sealed
 */
@inheritSerialization(() => WritableBactoforceUser)
export class BactoforceUser extends WritableBactoforceUser {
  @autoserializeAs(() => Boolean)
  public readonly hasSignature: boolean;

  @autoserializeAs(() => SimpleLanguage)
  public override language: Readonly<Existing<SimpleLanguage>>;

  @autoserializeAs(() => SimpleBactoforceLocation)
  public override primaryBactoforceLocation: Readonly<Existing<SimpleBactoforceLocation>>;
}

export type IdOnlyAdminUser = IdOnlyUser;

@inheritSerialization(() => BaseWritableBactoforceUser)
export class WritableAdminUser extends BaseWritableBactoforceUser<UserAccountType.Admin> {
  @autoserializeAsJson()
  public override readonly type = UserAccountType.Admin;

  @autoserializeAs(() => Boolean)
  public override readonly isInspector = false;

  public static createNew(firstName: string,
                          lastName: string,
                          email: string,
                          language: Readonly<Existing<CodeOnlyLanguage>>,
                          primaryBactoforceLocation: Readonly<Existing<IdOnlyBactoforceLocation>>): New<WritableAdminUser> {
    const user = this._createNew(firstName, lastName, email, language);
    user.primaryBactoforceLocation = primaryBactoforceLocation;
    return user;
  }
}

/**
 * @sealed
 */
@inheritSerialization(() => WritableAdminUser)
export class AdminUser extends WritableAdminUser {
  @autoserializeAs(() => Boolean)
  public readonly hasSignature: boolean;

  @autoserializeAs(() => SimpleLanguage)
  public override language: Readonly<Existing<SimpleLanguage>>;

  @autoserializeAs(() => SimpleBactoforceLocation)
  public override primaryBactoforceLocation: Readonly<Existing<SimpleBactoforceLocation>>;
}

export type IdOnlyCustomerUser = IdOnlyUser;

@inheritSerialization(() => BaseUser)
export class WritableCustomerUser extends BaseUser<UserAccountType.Customer> {
  @autoserializeAsJson()
  public override readonly type = UserAccountType.Customer;
}

/**
 * @sealed
 */
@inheritSerialization(() => WritableCustomerUser)
export class CreatableCustomerUser extends WritableCustomerUser {
  @autoserializeAs(() => WritableCustomerUserLocationAssignment)
  public locationAssignment: New<WritableCustomerUserLocationAssignment>;

  public static createNew(
    firstName: string,
    lastName: string,
    email: string,
    language: Readonly<Existing<CodeOnlyLanguage>>,
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    locationAssignment: Readonly<New<WritableCustomerUserLocationAssignment>>,
  ): New<CreatableCustomerUser> {
    const user = this._createNew(firstName, lastName, email, language);
    user.locationAssignment = locationAssignment;
    return asNew(user);
  }
}

/**
 * @sealed
 */
@inheritSerialization(() => WritableCustomerUser)
export class CustomerUser extends WritableCustomerUser {
  @autoserializeAs(() => SimpleLanguage)
  public override language: Readonly<Existing<SimpleLanguage>>;
}

export type IdOnlyThirdPartyUser = IdOnlyUser;

@inheritSerialization(() => BaseUser)
export class WritableThirdPartyUser extends BaseUser<UserAccountType.ThirdParty> {
  @autoserializeAsJson()
  public override readonly type = UserAccountType.ThirdParty;

  @autoserializeAs(() => IdOnlyThirdPartyCompany)
  public thirdPartyCompany: Existing<IdOnlyThirdPartyCompany>;

  public static createNew(
    firstName: string,
    lastName: string,
    email: string,
    language: Readonly<Existing<CodeOnlyLanguage>>,
    thirdPartyCompany: Readonly<Existing<IdOnlyThirdPartyCompany>>,
  ): New<WritableThirdPartyUser> {
    const user = this._createNew(firstName, lastName, email, language);
    user.thirdPartyCompany = thirdPartyCompany;
    return asNew(user);
  }
}

/**
 * @sealed
 */
@inheritSerialization(() => WritableThirdPartyUser)
export class ThirdPartyUser extends WritableThirdPartyUser {
  @autoserializeAs(() => SimpleLanguage)
  public override language: Readonly<Existing<SimpleLanguage>>;

  @autoserializeAs(() => SimpleThirdPartyCompany)
  public override thirdPartyCompany: Readonly<Existing<SimpleThirdPartyCompany>>;
}

export type AnyBactoforceUser = AdminUser | BactoforceUser;

export type AnyWritableUser = WritableAdminUser | WritableBactoforceUser | WritableCustomerUser | WritableThirdPartyUser;
export type AnyUser = AdminUser | BactoforceUser | CustomerUser | ThirdPartyUser;

type UsersByType = {
  readonly type: UserAccountType.Admin,
  readonly user: AdminUser
} | {
  readonly type: UserAccountType.Bactoforce,
  readonly user: BactoforceUser
} | {
  readonly type: UserAccountType.Customer,
  readonly user: CustomerUser
} | {
  readonly type: UserAccountType.ThirdParty,
  readonly user: ThirdPartyUser
};

export type UserOfType<TUserType extends UserAccountType> = (UsersByType & { readonly type: TUserType })["user"];

export type TypeOfUser<TUser extends AnyUser> = TUser["type"];
